import { createElement, useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { post, put } from "@common/api/apiShared";
import { IRiskAreaDialog } from "./RiskAreaDialog";
import { isNullOrWhitespace } from "@common/stringOperations";
import { getEnumValues } from "@common/enumOperations";
import { Result } from "@common/api/result";
import { RiskAreaAssessmentLevel, RiskAreaConfigChangeKind, RiskAreaFrameChangeImpact, RiskAreaSaveModel } from "./RiskAreaDialog.types";
import { Trans } from "react-i18next";
import { useDotLegalMutation, useDotLegalPostGetQuery, useDotLegalPostGetQueryArray } from "@common/hooks/dotLegalReactQuery";
import { buildDefaultMatrix } from "./riskAreaMatrix/riskAreaMatrixPreset";
import { getDefaultRiskAreaLevelIndicators, RiskLevelIndicator } from "./riskLevels/riskLevelIndicators";
import { useResponsibleQuery } from "../../../user/hooks/useResponsibleQuery";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useScaleMapping } from "./frameChange/FrameChangeConfirmationStep.hooks";

export const riskAreaScaleOptions = [3, 4, 5, 6];

export type RiskAreaDialogStep = "configure" | "confirm";

export const defaultScale = 5;

type TranslateLabel = (key: string) => string;

const consequenceLabelKeysByScale: Record<number, string[]> = {
    3: ["riskAreaLevelLabelNegligible", "riskAreaLevelLabelModerate", "riskAreaLevelLabelCritical"],
    4: ["riskAreaLevelLabelNegligible", "riskAreaLevelLabelMinor", "riskAreaLevelLabelSignificant", "riskAreaLevelLabelCritical"],
    5: ["riskAreaLevelLabelNegligible", "riskAreaLevelLabelMinor", "riskAreaLevelLabelModerate", "riskAreaLevelLabelSignificant", "riskAreaLevelLabelCritical"],
    6: [
        "riskAreaLevelLabelNegligible",
        "riskAreaLevelLabelMinor",
        "riskAreaLevelLabelModerate",
        "riskAreaLevelLabelSignificant",
        "riskAreaLevelLabelCritical",
        "riskAreaLevelLabelCatastrophic",
    ],
};

const probabilityLabelKeysByScale: Record<number, string[]> = {
    3: ["riskAreaLevelLabelLow", "riskAreaLevelLabelModerate", "riskAreaLevelLabelHigh"],
    4: ["riskAreaLevelLabelVeryLow", "riskAreaLevelLabelLow", "riskAreaLevelLabelHigh", "riskAreaLevelLabelVeryHigh"],
    5: ["riskAreaLevelLabelVeryLow", "riskAreaLevelLabelLow", "riskAreaLevelLabelModerate", "riskAreaLevelLabelHigh", "riskAreaLevelLabelVeryHigh"],
    6: [
        "riskAreaLevelLabelVeryLow",
        "riskAreaLevelLabelLow",
        "riskAreaLevelLabelModerate",
        "riskAreaLevelLabelHigh",
        "riskAreaLevelLabelVeryHigh",
        "riskAreaLevelLabelAlmostCertain",
    ],
};

const riskLabelKeysByScale: Record<number, string[]> = {
    3: ["riskAreaLevelLabelLow", "riskAreaLevelLabelModerate", "riskAreaLevelLabelHigh"],
    4: ["riskAreaLevelLabelVeryLow", "riskAreaLevelLabelLow", "riskAreaLevelLabelHigh", "riskAreaLevelLabelVeryHigh"],
    5: ["riskAreaLevelLabelVeryLow", "riskAreaLevelLabelLow", "riskAreaLevelLabelModerate", "riskAreaLevelLabelHigh", "riskAreaLevelLabelVeryHigh"],
    6: [
        "riskAreaLevelLabelVeryLow",
        "riskAreaLevelLabelLow",
        "riskAreaLevelLabelModerate",
        "riskAreaLevelLabelHigh",
        "riskAreaLevelLabelVeryHigh",
        "riskAreaLevelLabelAlmostCertain",
    ],
};

export function useRiskAreaDialogDataMapping(props: IRiskAreaDialog) {
    const snackbar = useDotLegalSnackbar();
    const { translateString, translateRiskAreaAssessmentLevel } = useTranslation();
    const { userProfileId } = useUserContext();
    const [errors, setErrors] = useState({ nameError: "", responsibleError: "", appetiteError: "", matrixError: "" });
    const [inProgress, setInProgress] = useState(false);
    const [viewModel, setViewModel] = useState<RiskAreaSaveModel>(props.selectedRiskArea ?? createDefaultRiskAreaModel(defaultScale, translateString));

    // Parked frames of the scales visited in this dialog, so switching scale to compare instruments costs nothing.
    // Not seeded with the saved area: the frame is parked on the way out, which returns the user's own edits at
    // that scale rather than reverting them to what was saved.
    const [scaleDrafts, setScaleDrafts] = useState<RiskAreaFrameDrafts>({});

    // Re-cutting the instrument is a second step of this dialog rather than a dialog of its own: stacking a modal
    // on a modal buries the form the user was working in, and the confirmation is part of the same act as saving.
    const [step, setStep] = useState<RiskAreaDialogStep>("configure");
    const [changeReason, setChangeReason] = useState("");
    const [reasonError, setReasonError] = useState("");

    const responsibleQuery = useResponsibleQuery();
    const responsibleOptions = responsibleQuery.data(viewModel.responsibleId) ?? [];

    const original = props.selectedRiskArea;
    const isExistingArea = original !== undefined && original.id !== "";
    const changeKind = original ? deriveChangeKind(original, viewModel) : RiskAreaConfigChangeKind.None;

    // The criteria are authored on the form, so the only thing the confirmation still has to settle is how the
    // scores already given are carried onto a new scale.
    const scaleMapping = useScaleMapping(isExistingArea ? original : undefined, viewModel);

    // The backend restricts a frame change to the area's responsible on top of the edit permission, and refuses it
    // with a 403 that navigates the whole application to the forbidden page, taking this dialog with it. Locking
    // the two zones up front means the wizard can never be filled in only to be thrown away. Compared against the
    // saved responsible rather than the edited one, mirroring the handler: it authorises before it writes, so
    // handing yourself the area in the same edit does not unlock the criteria.
    const canChangeFrame = !isExistingArea || original.responsibleId === userProfileId;

    // Asked for with the area's own configuration rather than the edited one, so the per-cell badges show where
    // the assessments stand today. The other figures are recomputed against the proposal on confirmation.
    const currentFrameImpact = useDotLegalPostGetQuery<RiskAreaFrameChangeImpact>(`/risk-areas/${original?.id}/frame-change-impact`, original, {
        enabled: isExistingArea,
    });

    // Asked for with the edited configuration, and only once the user has committed to saving it, so the
    // confirmation shows what this specific change would do. The query key carries the part of the proposal the
    // answer depends on: the request body is not part of the key, so without it a cancel-edit-confirm round
    // would be served the previous proposal's numbers.
    const proposedModel: RiskAreaSaveModel = {
        ...viewModel,
        scaleMapping: scaleMapping.isReBasing ? scaleMapping.model : null,
    };

    const proposedFrameImpact = useDotLegalPostGetQueryArray<RiskAreaFrameChangeImpact>(
        ["riskAreaFrameChangeImpact", viewModel.id, viewModel.scale, viewModel.appetite, JSON.stringify(viewModel.matrix), JSON.stringify(scaleMapping.model)],
        `/risk-areas/${viewModel.id}/frame-change-impact`,
        proposedModel,
        { enabled: step === "confirm" && scaleMapping.isValid }
    );

    const saveMutation = useDotLegalMutation(save, {
        onSuccess: (response: Result<string>) => {
            if (response.success()) {
                props.onHideDialog();
                props.onSave(response.value());
                setInProgress(false);
                snackbar.show(createElement(Trans, { i18nKey: "dataCreated", values: { name: viewModel.name } }));
            } else {
                setInProgress(false);
                snackbar.show(translateString("riskAreaExists"), "warning");
            }
        },
    });

    function save() {
        return post<string>("/risk-areas/", viewModel);
    }

    const editMutation = useDotLegalMutation(update);
    // The model to save is passed in rather than read back off the view model: a confirmation step settles the
    // reason and the mapping in the same tick the save starts, so those state updates have not landed yet.
    const updateRiskArea = (model: RiskAreaSaveModel) => {
        editMutation.mutateAsync(model, {
            onSuccess: (response: Result<string>) => {
                if (response.success()) {
                    snackbar.show(translateString("riskAreaUpdated", { name: viewModel.name }));
                    props.onHideDialog();
                    props.onSave("");
                } else if (response.hasError("riskAreaAssessmentLevelInUse")) {
                    setInProgress(false);
                    snackbar.show(translateString("riskAreaAssessmentLevelInUse"), "warning");
                } else {
                    setInProgress(false);
                    snackbar.show(translateString("riskAreaExists"), "warning");
                }
            },
        });
    };

    function update(model: RiskAreaSaveModel) {
        return put<string>(`/risk-areas/${model.id}/`, model);
    }

    const setName = (name: string) => {
        setErrors({ ...errors, nameError: isNullOrWhitespace(name) ? translateString("fieldRequired") : "" });
        setViewModel({ ...viewModel, name });
    };

    const setResponsible = (responsibleId: string | null) => {
        setErrors({ ...errors, responsibleError: responsibleId ? "" : translateString("fieldRequired") });
        setViewModel({ ...viewModel, responsibleId });
    };

    const setAssessmentLevel = (assessmentLevel: RiskAreaAssessmentLevel) => {
        setViewModel({ ...viewModel, assessmentLevel });
    };

    const assessmentLevelOptions = getEnumValues(RiskAreaAssessmentLevel).map((level) => ({
        id: level.toString(),
        name: translateRiskAreaAssessmentLevel(level),
    }));

    // A new scale re-cuts the criteria, so the form resizes in place and the whole instrument stays editable: the
    // levels, the matrix and the appetite are authored here, at the new scale, before anything is committed. The
    // scores already given are a separate question, settled by the mapping on the confirmation step.
    const setScale = (scale: number) => {
        const switched = switchScale(viewModel, scale, scaleDrafts, translateString);
        // The select fires on re-picking the current scale too, which switchScale answers with the model as it was.
        if (switched.model === viewModel) return;

        setScaleDrafts(switched.drafts);
        setViewModel(switched.model);
        // A returned-to frame can be complete, so it clears the errors its own fields no longer have, the way
        // editing those fields would.
        setErrors({
            ...errors,
            appetiteError: switched.model.appetite === null ? errors.appetiteError : "",
            matrixError: matrixHasEmptyCells(switched.model.matrix) ? errors.matrixError : "",
        });
    };

    const setAppetite = (appetite: number | null) => {
        setErrors({ ...errors, appetiteError: appetite === null ? errors.appetiteError : "" });
        setViewModel({ ...viewModel, appetite: appetite === null ? null : clampToScale(appetite, viewModel.scale) });
    };

    const setConsequenceLevel = (index: number, value: string) => {
        setViewModel({ ...viewModel, consequenceLevels: replaceAt(viewModel.consequenceLevels, index, value) });
    };

    const setProbabilityLevel = (index: number, value: string) => {
        setViewModel({ ...viewModel, probabilityLevels: replaceAt(viewModel.probabilityLevels, index, value) });
    };

    const setRiskLevel = (index: number, value: string) => {
        setViewModel({ ...viewModel, riskLevels: replaceAt(viewModel.riskLevels, index, value) });
    };

    const setRiskLevelIndicator = (index: number, indicator: RiskLevelIndicator) => {
        setViewModel({ ...viewModel, riskLevelIndicators: replaceAt(viewModel.riskLevelIndicators, index, indicator) });
    };

    const setMatrixCell = (consequenceIndex: number, probabilityIndex: number, value: number | null) => {
        const matrix = viewModel.matrix.map((row, c) =>
            c === consequenceIndex ? replaceAt(row, probabilityIndex, value === null ? null : clampToScale(value, viewModel.scale)) : row
        );
        setErrors({ ...errors, matrixError: matrixHasEmptyCells(matrix) ? errors.matrixError : "" });
        setViewModel({ ...viewModel, matrix });
    };

    const validateAndSave = () => {
        const newErrors = {
            nameError: isNullOrWhitespace(viewModel.name) ? translateString("fieldRequired") : "",
            responsibleError: viewModel.responsibleId ? "" : translateString("fieldRequired"),
            appetiteError: viewModel.appetite === null ? translateString("fieldRequired") : "",
            matrixError: matrixHasEmptyCells(viewModel.matrix) ? translateString("riskAreaMatrixCellsRequired") : "",
        };
        setErrors(newErrors);
        if (newErrors.nameError || newErrors.responsibleError || newErrors.appetiteError || newErrors.matrixError) {
            return;
        }

        // Re-cutting the instrument is a different act from saving a setting, so it takes a confirmation step
        // that shows the blast radius and collects the reason before anything is written.
        if (isEdit() && requiresFrameChangeConfirmation(changeKind)) {
            setChangeReason("");
            setReasonError("");
            if (scaleMapping.isReBasing) scaleMapping.seed();
            setStep("confirm");
            return;
        }

        setInProgress(true);
        if (isEdit()) updateRiskArea(viewModel);
        else saveMutation.mutateAsync();
    };

    const changeReasonEntered = (value: string) => {
        setChangeReason(value);
        if (reasonError && !isNullOrWhitespace(value)) setReasonError("");
    };

    // Back to the form with nothing written: the edits are still there to keep working on, and the mapping is
    // re-seeded next time round rather than remembered from an abandoned attempt.
    const backToConfigure = () => {
        setReasonError("");
        setStep("configure");
    };

    // Confirming is the save, so the figures the user approved keep describing exactly what gets written.
    const confirmFrameChange = () => {
        const reasonMissing = isNullOrWhitespace(changeReason);
        setReasonError(reasonMissing ? translateString("riskAreaFrameChangeReasonRequired") : "");

        if (reasonMissing || !scaleMapping.isValid) return;

        setInProgress(true);
        updateRiskArea({ ...proposedModel, changeReason: changeReason.trim() });
    };

    function isEdit() {
        return props.selectedRiskArea !== undefined && props.selectedRiskArea.id !== "";
    }

    // The counts sit on the cells of the current frame, so they only line up with the matrix while the scale is
    // unchanged. A re-base re-cuts the grid, and where the scores land is the confirmation step's answer, not
    // something the form can badge.
    const cellBadges = scaleMapping.isReBasing ? undefined : currentFrameImpact.data?.assessmentsPerCell;

    // What the appetite costs right now, answered from the area's own occupancy instead of another round trip. The
    // board's question about an appetite is how many risks sit above the line, so it is worth answering while the
    // line is being moved rather than only after. A scale change moves the cells themselves, and then the answer
    // needs the approved mapping, so it waits for the confirmation step's figures.
    const overAppetite =
        cellBadges === undefined || viewModel.appetite === null ? undefined : countOverAppetite(cellBadges, viewModel.matrix, viewModel.appetite);

    return {
        inProgress,
        errors,
        viewModel,
        isEdit,
        setName,
        setResponsible,
        responsibleOptions,
        isLoadingResponsibles: responsibleQuery.isLoading,
        setAssessmentLevel,
        assessmentLevelOptions,
        setScale,
        setAppetite,
        setConsequenceLevel,
        setProbabilityLevel,
        setRiskLevel,
        setRiskLevelIndicator,
        setMatrixCell,
        validateAndSave,
        changeKind,
        canChangeFrame,
        cellBadges,
        overAppetite,
        step,
        backToConfigure,
        confirmFrameChange,
        changeReason,
        changeReasonEntered,
        reasonError,
        frameChangeImpact: proposedFrameImpact.data,
        isLoadingConfirmation: proposedFrameImpact.isLoading,
        canConfirm: scaleMapping.isValid,
        scaleMapping,
    };
}

export function createDefaultRiskAreaModel(scale: number, translate: TranslateLabel): RiskAreaSaveModel {
    return {
        id: "",
        name: "",
        responsibleId: null,
        assessmentLevel: RiskAreaAssessmentLevel.Entity,
        scale,
        appetite: Math.ceil(scale / 2),
        ...scaleDerivedFields(scale, translate),
    };
}

// The scale-dependent fields (level labels, indicators, matrix) are rebuilt the same way whether creating a
// new area or changing an existing one's scale, so both paths share this.
function scaleDerivedFields(scale: number, translate: TranslateLabel) {
    return {
        consequenceLevels: defaultLabels(consequenceLabelKeysByScale, scale, translate),
        probabilityLevels: defaultLabels(probabilityLabelKeysByScale, scale, translate),
        riskLevels: defaultLabels(riskLabelKeysByScale, scale, translate),
        riskLevelIndicators: getDefaultRiskAreaLevelIndicators(scale),
        matrix: buildDefaultMatrix(scale),
    };
}

export interface OverAppetiteCount {
    count: number;
    total: number;
}

// Counts the assessments the proposed matrix and appetite would leave above the line. The grid is the area's
// occupancy on its current frame, so this only answers while the scale is unchanged: a different scale moves the
// cells themselves, and that answer needs the approved mapping.
export function countOverAppetite(assessmentsPerCell: number[][], matrix: (number | null)[][], appetite: number): OverAppetiteCount {
    let count = 0;
    let total = 0;

    assessmentsPerCell.forEach((row, consequenceIndex) =>
        row.forEach((assessments, probabilityIndex) => {
            total += assessments;
            const level = matrix[consequenceIndex]?.[probabilityIndex];
            if (level != null && level > appetite) count += assessments;
        })
    );

    return { count, total };
}

export function matrixHasEmptyCells(matrix: (number | null)[][]): boolean {
    return matrix.some((row) => row.some((cell) => cell === null));
}

// A scale change restarts the matrix: cropping or padding an existing one cannot satisfy the preset invariants
// (the top band disappears when shrinking), so it is rebuilt from the preset. The level labels are not reset
// with it, and the appetite is cleared rather than clamped.
export function resizeModelToScale(model: RiskAreaSaveModel, scale: number, translate: TranslateLabel): RiskAreaSaveModel {
    const preset = scaleDerivedFields(scale, translate);

    return {
        ...model,
        scale,
        ...preset,
        // Clamping keeps the number while inverting its meaning: an appetite of 3 leaves most of a 1-5 area over
        // appetite but only the top level of a 1-3 one. Clearing it forces a deliberate re-pick.
        appetite: null,
        consequenceLevels: preserveOverlappingLabels(model.consequenceLevels, preset.consequenceLevels),
        probabilityLevels: preserveOverlappingLabels(model.probabilityLevels, preset.probabilityLevels),
        riskLevels: preserveOverlappingLabels(model.riskLevels, preset.riskLevels),
    };
}

// What a change of scale re-cuts, as opposed to the name and the responsible, which mean the same thing
// whichever instrument is in use. The scale itself is the key these are held under, not one of them.
type RiskAreaFrame = Pick<RiskAreaSaveModel, "appetite" | "consequenceLevels" | "probabilityLevels" | "riskLevels" | "riskLevelIndicators" | "matrix">;

// The frame the user last had at each scale, so a scale they return to is picked up rather than re-cut.
type RiskAreaFrameDrafts = Record<number, RiskAreaFrame>;

function toRiskAreaFrame(model: RiskAreaSaveModel): RiskAreaFrame {
    return {
        appetite: model.appetite,
        consequenceLevels: model.consequenceLevels,
        probabilityLevels: model.probabilityLevels,
        riskLevels: model.riskLevels,
        riskLevelIndicators: model.riskLevelIndicators,
        matrix: model.matrix,
    };
}

// Choosing a scale is a comparison, not yet a decision: the two instruments are authored and their impact
// compared before either is committed. So the frame being left is parked and the frame being returned to is
// picked up as it was left, which makes the round trip a genuine no-op. That matters most for the saved scale,
// where a re-cut matrix would otherwise classify as a semantic change and write a revision per assessment for
// what the user meant as an undo. A scale never authored in this session still starts from the preset: a zone
// map cannot be stretched onto a different grid, so it has to be re-authored, and the appetite re-picked.
export function switchScale(
    model: RiskAreaSaveModel,
    scale: number,
    drafts: RiskAreaFrameDrafts,
    translate: TranslateLabel
): { model: RiskAreaSaveModel; drafts: RiskAreaFrameDrafts } {
    // Staying put is not a switch: parking and picking up the same scale in one call would hand back the frame
    // parked on the way out of it and lose the edits made since.
    if (scale === model.scale) return { model, drafts };

    const returningTo = drafts[scale];

    return {
        model: returningTo ? { ...model, scale, ...returningTo } : resizeModelToScale(model, scale, translate),
        drafts: { ...drafts, [model.scale]: toRiskAreaFrame(model) },
    };
}

// Keeps whatever the customer wrote for the levels that still exist on the new scale and fills the rest from
// the preset. Replacing hand-written labels with English defaults on every scale change is pure loss.
export function preserveOverlappingLabels(existing: string[], preset: string[]): string[] {
    return preset.map((presetLabel, index) => existing[index] ?? presetLabel);
}

// Mirrors the backend RiskAreaConfigChange.Classify. The dialog needs the same verdict before saving, to decide
// whether the change is a plain save or a re-cut of the instrument that has to be confirmed and justified.
export function deriveChangeKind(original: RiskAreaSaveModel, current: RiskAreaSaveModel): RiskAreaConfigChangeKind {
    if (original.scale !== current.scale) return RiskAreaConfigChangeKind.Structural;

    if (original.appetite !== current.appetite || matrixDiffers(original.matrix, current.matrix)) return RiskAreaConfigChangeKind.Semantic;

    if (
        labelsDiffer(original.consequenceLevels, current.consequenceLevels) ||
        labelsDiffer(original.probabilityLevels, current.probabilityLevels) ||
        labelsDiffer(original.riskLevels, current.riskLevels) ||
        labelsDiffer(original.riskLevelIndicators, current.riskLevelIndicators)
    )
        return RiskAreaConfigChangeKind.Cosmetic;

    return RiskAreaConfigChangeKind.None;
}

export function requiresFrameChangeConfirmation(kind: RiskAreaConfigChangeKind): boolean {
    return kind === RiskAreaConfigChangeKind.Semantic || kind === RiskAreaConfigChangeKind.Structural;
}

function matrixDiffers(original: (number | null)[][], current: (number | null)[][]): boolean {
    return original.some((row, consequenceIndex) => labelsDiffer(row, current[consequenceIndex]));
}

function labelsDiffer<T>(original: T[], current: T[]): boolean {
    return original.length !== current.length || original.some((value, index) => value !== current[index]);
}

function defaultLabels(keysByScale: Record<number, string[]>, scale: number, translate: TranslateLabel): string[] {
    return (keysByScale[scale] ?? []).map((key) => translate(key));
}

function replaceAt<T>(values: T[], index: number, value: T): T[] {
    return values.map((current, i) => (i === index ? value : current));
}

function clampToScale(value: number, scale: number): number {
    if (isNaN(value)) return 1;
    return Math.min(scale, Math.max(1, Math.round(value)));
}
